import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import {
    Eye,
    EyeOff,
    Loader2,
    ArrowLeft,
    Shield,
    CheckCircle,
    Key
} from "lucide-react";
import api from "../../api/axios";

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const { token } = useParams();
    const [mounted, setMounted] = useState(false);

    const { success, error } = useToast();

    const [formData, setFormData] = useState({
        password: "",
        confirmPassword: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const [resetSuccess, setResetSuccess] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setErrors({
            ...errors,
            [e.target.name]: "",
        });
    };

    const validate = () => {
        let newErrors = {};

        if (!formData.password) {
            newErrors.password = "Password is required";
        } else if (formData.password.length < 8) {
            newErrors.password = "Minimum 8 characters required";
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
            newErrors.password = "Must include uppercase, lowercase & number";
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = "Confirm your password";
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await api.post("/auth/reset-password", {
                token,
                password: formData.password,
            });
            if (response?.data?.success) {
                setLoading(false);
                setResetSuccess(true);
                success("Password reset successfully! 🎉 You can now sign in with your new password.");

                setTimeout(() => {
                    navigate("/?p=login");
                }, 2000);
            }

        } catch (errors) {
            error(errors?.response?.data?.message || "Reset link expired or invalid");
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4 relative overflow-hidden">

            <div className="absolute inset-0 overflow-hidden">
                <div className={`absolute w-[800px] h-[800px] rounded-full bg-indigo-300/20 blur-[160px] top-[-500px] left-[-300px] transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute w-[800px] h-[800px] rounded-full bg-blue-300/20 blur-[150px] bottom-[-400px] right-[-250px] transition-all duration-1000 delay-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute w-[700px] h-[700px] rounded-full bg-violet-300/20 blur-[140px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 delay-500 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute w-[500px] h-[500px] rounded-full bg-pink-200/25 blur-[120px] top-1/4 right-1/4 transition-all duration-1000 delay-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
            </div>

            {/* Main Card */}
            <div className={`w-full max-w-4xl relative  transition-all duration-1000 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
                    <div className="grid md:grid-cols-2">

                        {/* Left Side - Security Info */}
                        <div className="relative p-8 md:p-10 bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col">

                            {/* Logo */}
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center shadow-sm">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m17 2-5 5-5-5"></path>
                                        <rect width="20" height="15" x="2" y="7" rx="2"></rect>
                                    </svg>
                                </div>
                                <h1 className="text-2xl font-semibold text-gray-800">
                                    SocialTV
                                </h1>
                            </div>

                            {/* Security Content */}
                            <div className="relative z-10 flex-1 flex flex-col">
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                                    Create a strong<br />
                                    <span className="text-indigo-600">
                                        new password
                                    </span>
                                </h2>

                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    Your password should be unique and secure to protect your account.
                                </p>

                                {/* Password Requirements */}
                                <div className="space-y-4 mb-6">
                                    <h3 className="font-medium text-gray-700">Password requirements:</h3>
                                    <div className="space-y-2">
                                        {[
                                            { text: "At least 8 characters long", check: formData.password.length >= 8 },
                                            { text: "Contains uppercase letter (A-Z)", check: /[A-Z]/.test(formData.password) },
                                            { text: "Contains lowercase letter (a-z)", check: /[a-z]/.test(formData.password) },
                                            { text: "Contains number (0-9)", check: /\d/.test(formData.password) },
                                            { text: "Passwords match", check: formData.password && formData.confirmPassword && formData.password === formData.confirmPassword }
                                        ].map((req, index) => (
                                            <div key={index} className="flex items-center gap-2 text-sm">
                                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.check
                                                    ? 'bg-green-100 text-green-500'
                                                    : 'bg-gray-100 text-gray-300'
                                                    }`}>
                                                    {req.check ? (
                                                        <CheckCircle size={12} />
                                                    ) : (
                                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                                                    )}
                                                </div>
                                                <span className={`${req.check ? 'text-gray-700' : 'text-gray-400'}`}>
                                                    {req.text}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Security Tips */}
                                <div className="mt-auto pt-6 border-t border-indigo-100">
                                    <div className="flex items-start gap-2">
                                        <Shield size={16} className="text-indigo-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-gray-500">
                                            Never share your password with anyone. SocialTV will never ask for your password via email or phone.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Reset Password Form */}
                        <div className="p-8 md:p-10 bg-white flex flex-col">

                            {/* Back to Login */}
                            <button
                                onClick={() => navigate("/?p=login")}
                                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 group w-fit"
                            >
                                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                                Back to sign in
                            </button>

                            {/* Success State */}
                            {resetSuccess ? (
                                <div className="flex flex-col items-center justify-center py-8 animate-slideIn">
                                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                                        <CheckCircle className="w-10 h-10 text-green-500" />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                                        Password Reset!
                                    </h4>
                                    <p className="text-sm text-gray-500 text-center mb-6">
                                        Your password has been successfully updated.<br />
                                        Redirecting you to sign in...
                                    </p>
                                    <div className="w-12 h-1 bg-gray-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-indigo-500 animate-progress"></div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Form Header */}
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                                            Set new password
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                            Choose a strong password that you haven't used before.
                                        </p>
                                    </div>

                                    {/* Form */}
                                    <form onSubmit={handleSubmit} className="space-y-5">

                                        {/* New Password */}
                                        <div className="space-y-1.5">
                                            <label className="block text-sm font-medium text-gray-700">
                                                New Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    onFocus={() => setFocusedField('password')}
                                                    onBlur={() => setFocusedField(null)}
                                                    placeholder="Enter new password"
                                                    className={`w-full px-4 py-3 rounded-lg border border-gray-200 transition-all duration-200 pl-10 pr-10
                                                        ${errors.password
                                                            ? "border-red-300 bg-red-50/50"
                                                            : "border-gray-200 bg-gray-50"
                                                        }
                                                        outline-none focus:ring-1  focus:border-indigo-300 focus:bg-indigo-50/30 text-sm`}
                                                />
                                                <Key className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors
                                                    ${errors.password
                                                        ? "text-red-400"
                                                        : focusedField === 'password'
                                                            ? "text-indigo-400"
                                                            : "text-gray-400"
                                                    }`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors
                                                        ${focusedField === 'password'
                                                            ? "text-indigo-400"
                                                            : "text-gray-400 hover:text-gray-600"
                                                        }`}
                                                >
                                                    {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </button>
                                            </div>
                                            {errors.password && (
                                                <p className="text-red-500 text-xs mt-1 animate-slideIn">
                                                    {errors.password}
                                                </p>
                                            )}
                                        </div>

                                        {/* Confirm Password */}
                                        <div className="space-y-1.5">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Confirm Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={handleChange}
                                                    onFocus={() => setFocusedField('confirmPassword')}
                                                    onBlur={() => setFocusedField(null)}
                                                    placeholder="Confirm new password"
                                                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 pl-10 pr-10
                                                        ${errors.confirmPassword
                                                            ? "border-red-300 bg-red-50/50"
                                                            : focusedField === 'confirmPassword'
                                                                ? "border-indigo-300 bg-indigo-50/30"
                                                                : "border-gray-200 bg-gray-50"
                                                        }
                                                        outline-none focus:ring-1
                                                        ${errors.confirmPassword
                                                            ? "focus:ring-red-100"
                                                            : "focus:ring-indigo-100"
                                                        }
                                                        text-sm`}
                                                />
                                                <Key className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors
                                                    ${errors.confirmPassword
                                                        ? "text-red-400"
                                                        : focusedField === 'confirmPassword'
                                                            ? "text-indigo-400"
                                                            : "text-gray-400"
                                                    }`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors
                                                        ${focusedField === 'confirmPassword'
                                                            ? "text-indigo-400"
                                                            : "text-gray-400 hover:text-gray-600"
                                                        }`}
                                                >
                                                    {showConfirmPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </button>
                                            </div>
                                            {errors.confirmPassword && (
                                                <p className="text-red-500 text-xs mt-1 animate-slideIn">
                                                    {errors.confirmPassword}
                                                </p>
                                            )}
                                            {!errors.confirmPassword && formData.confirmPassword && formData.password === formData.confirmPassword && (
                                                <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                                                    <CheckCircle size={12} />
                                                    Passwords match
                                                </p>
                                            )}
                                        </div>

                                        {/* Submit Button */}
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-all font-medium text-sm disabled:opacity-70 disabled:cursor-not-allowed mt-6"
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={16} />
                                                    Resetting password...
                                                </>
                                            ) : (
                                                "Reset Password"
                                            )}
                                        </button>

                                        {/* Token Status */}
                                        {token && (
                                            <p className="text-xs text-center text-gray-400 mt-4">
                                                Valid reset link detected
                                            </p>
                                        )}
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add custom animations */}
            <style jsx>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes progress {
                    from { width: 0%; }
                    to { width: 100%; }
                }
                .animate-slideIn {
                    animation: slideIn 0.3s ease-out;
                }
                .animate-progress {
                    animation: progress 2s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default ResetPasswordPage;